import { useContext, useMemo, useState } from "react";

import { TenantContext } from "@nens/ranalib";

import { VisibleLayer } from "@/components/layer-control";
import {
  useFileDescriptorQuery,
  useFileDescriptorViewQuery,
} from "@/queries/query-file-descriptors";
import { useFileVectorStyleQueryV1 } from "@/queries/query-style-sprite";
import {
  getVectorDescriptorLayers,
  getVectorDescriptorSprite,
} from "@/utils/get-vector-descriptor-layers-and-sprite";
import { isTypedDescriptor } from "@/utils/is-descriptor-type";

// This function takes an descriptor id and gets, sets and returns all the necessary information to visualize said descriptor.

type UseVectorPreviewProps = {
  descriptorId: string | undefined;
  ignoreStyle?: boolean;
};

export const useVectorPreview = ({ descriptorId, ignoreStyle }: UseVectorPreviewProps) => {
  const { tenant } = useContext(TenantContext);

  const [visibleLayers, setVisibleLayers] = useState<VisibleLayer[]>([]);

  // GET: Descriptor - style.json - descriptor view

  const { data: descriptorFromQuery, isLoading: isLoadingDescriptor } =
    useFileDescriptorQuery(descriptorId);
  const descriptor = useMemo(
    () => (isTypedDescriptor(descriptorFromQuery, "vector") ? descriptorFromQuery : undefined),
    [descriptorFromQuery]
  );

  const { data: styleJson, isLoading: isLoadingStyleJson } = useFileVectorStyleQueryV1(
    ignoreStyle ? undefined : descriptorId
  );
  const { data: descriptorView, isLoading: isLoadingDescriptorView } = useFileDescriptorViewQuery(
    descriptorId,
    "pmtiles"
  );

  const isLoading = isLoadingDescriptor || isLoadingStyleJson || isLoadingDescriptorView;

  // Get the layers from the descriptor meta and set the visible layers

  const layers = useMemo(() => {
    const layers =
      descriptor && descriptorView
        ? getVectorDescriptorLayers({
            descriptorId: descriptor.id,
            meta: descriptor.meta,
            styleJson,
            descriptorView,
            styleId: null,
          })
        : [];

    setVisibleLayers(layers.map(({ descriptorId, layer }) => ({ descriptorId, layer })));

    return layers;
  }, [descriptor, descriptorView, styleJson]);

  const bounds = !isLoading
    ? (descriptor?.meta.envelope as [number, number, number, number]) // FUTURE - remove as when backend typing is more strict
    : undefined;

  const sprite =
    !isLoading && descriptorId && styleJson
      ? getVectorDescriptorSprite({ descriptorId, styleId: null, tenant, styleJson })
      : null;

  const sprites = sprite ? [sprite] : [];

  return {
    isLoading,
    sprites,
    layers,
    visibleLayers,
    setVisibleLayers,
    bounds,
  };
};
